import { GAMIFICATION_THRESHOLD_TYPES, MENU_TYPE_MED } from 'shared/constants';
import { getLogger } from 'src/utils/datadog-logger';
import type { BogoConditionItem, SpecialMenuCard, Specials } from 'src/gql/graphql';
import type { Product } from 'types/graphql-overrides';
import type { GamificationDetail } from 'src/types/types';

import type { Bundle, BundleItem, BundleProgress, BundleRequirement, BundleSource } from './bundle.types';

const INDIVIDUAL_PRODUCT_GROUP = 'individual';

/**
 * Provisional UX cap on products per bundle -- NOT a technical limit (Armageddon's generic ceiling
 * is 350). A one-click "Add Bundle to Cart" and a discovery card both degrade well below that.
 *
 * TODO(product): revisit once bundles have real production usage; this is an arbitrary starting
 * point, not a measured one.
 */
export const MAX_BUNDLE_ITEMS = 10;

/**
 * Reasons `getSpecialProductRequirements`/`buildBundle` can decline or adjust a special. Exported
 * so ECOMM-15150 (bundle telemetry) can reuse this taxonomy instead of re-deriving it.
 */
export type BundleDiagnosticReason =
  | 'ambiguous_product_match'
  | 'condition_resolution_incomplete'
  | 'discount_bundle_disabled'
  | 'duplicate_condition_merged'
  | 'duplicate_item_merged'
  | 'invalid_option_count'
  | 'logic_operator_not_and'
  | 'missing_unit_price'
  | 'no_conditions'
  | 'non_individual_condition'
  | 'product_not_found'
  | 'too_many_conditions'
  | 'unsupported_aggregate_threshold'
  | 'unsupported_condition_restriction';

const logger = getLogger('bundle-eligibility');

/**
 * Dev-mode breadcrumb for why a special was excluded/adjusted -- production ships no bundle
 * telemetry today, so this is the one place to see "why didn't my special show up as a bundle".
 * Gated on `!== 'production'` (not `=== 'development'`) so unit tests can observe it too.
 */
function logBundleDiagnostic(reason: BundleDiagnosticReason, context: Record<string, unknown> = {}): void {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  logger.debug(`Bundle diagnostic: ${reason}`, { reason, ...context });
}

function getSpecialId(special: Specials): string | undefined {
  return special._id ?? special.sourceId ?? special.specialId ?? undefined;
}

function unique(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => !!value))];
}

function resolveConditionQuantity(quantity: BogoConditionItem['quantity']): number {
  // `quantity` is server-provided and nullable; guard against missing, zero, negative, and
  // non-finite (e.g. NaN from bad data) values up front -- `Math.max(1, NaN)` is itself `NaN`, so
  // this can't be simplified to a bare `Math.max(1, quantity ?? 1)`.
  if (typeof quantity !== 'number' || !Number.isFinite(quantity)) {
    return 1;
  }
  return Math.max(1, Math.trunc(quantity));
}

/**
 * The deterministic eligibility gate: whether `special` can be a one-click shoppable bundle, and
 * if so, which products/quantities it requires. Returns `[]` for anything that isn't deterministic
 * (see `BundleDiagnosticReason`, logged via `logBundleDiagnostic` in dev mode).
 *
 * Rules, in order: (1) `discountBundle.enabled` and AND-logic conditions; (2) 1..MAX_BUNDLE_ITEMS
 * conditions; (3) every condition targets `productGroup: "individual"`; (4) no condition carries a
 * weight/weightOperator or exclusion predicate, or an enabled aggregate (totalQuantity/totalSpend/
 * totalWeight) gate -- none of these are validated against the resolved option/cart composition
 * below, so a condition using one fails closed rather than risk a bundle the real condition engine
 * won't actually honor; (5) every condition resolves to exactly one product ID -- several IDs
 * means "any of these," not "all of these," and can't power a one-click add; (6) one failing
 * condition rejects the whole special (all-or-nothing); (7) two conditions resolving to the same
 * product ID merge into one requirement with summed quantity.
 *
 * Known gap: a condition identified ONLY by `enterpriseProductId` (no local `productId`) resolves
 * here, but `useDispensaryProducts`' underlying query matches on local `_id` only, so it can never
 * actually be fetched -- see use-bundles.ts. Enterprise-wide bundles are out of scope for now.
 */
export function getSpecialProductRequirements(special: Specials): BundleRequirement[] {
  const specialId = getSpecialId(special);

  if (!special.discountBundle?.enabled) {
    logBundleDiagnostic('discount_bundle_disabled', { specialId });
    return [];
  }

  if (special.bogoConditionLogicOperator?.toLowerCase() !== 'and') {
    logBundleDiagnostic('logic_operator_not_and', { specialId, operator: special.bogoConditionLogicOperator });
    return [];
  }

  const conditions = special.bogoConditions ?? [];

  if (!conditions.length) {
    logBundleDiagnostic('no_conditions', { specialId });
    return [];
  }

  if (conditions.length > MAX_BUNDLE_ITEMS) {
    logBundleDiagnostic('too_many_conditions', {
      specialId,
      conditionCount: conditions.length,
      maxAllowed: MAX_BUNDLE_ITEMS,
    });
    return [];
  }

  const resolved = conditions.flatMap((condition, index) => {
    if (condition?.productGroup !== INDIVIDUAL_PRODUCT_GROUP) {
      logBundleDiagnostic('non_individual_condition', { specialId, index, productGroup: condition?.productGroup });
      return [];
    }

    // Weight/exclusion predicates aren't checked against the resolved option in buildBundle --
    // accepting them blindly could advertise a bundle for an option (e.g. the only priced one)
    // the real condition engine would reject (e.g. "must be the 3.5g option"). Fail closed.
    //
    // 'Any Weight'/'N/A'/nil all mean NO restriction (the admin wizard and the v4->v3 special
    // mapper both set `weight: 'Any Weight', weightOperator: 'equalTo'` on every plain product
    // selection, restricted or not -- see packages/shared/order/bogo/condition.js's own
    // formatConditionWeight), and the real engine never consults weightOperator on its own, only
    // alongside an actual restricting weight. Treating either as inherently restrictive here would
    // reject nearly every real bundle special, not just the ones with a genuine weight predicate.
    const hasWeightRestriction = !!condition.weight && condition.weight !== 'Any Weight' && condition.weight !== 'N/A';
    if (hasWeightRestriction || (condition.exclusions ?? []).length > 0) {
      logBundleDiagnostic('unsupported_condition_restriction', { specialId, index });
      return [];
    }

    // Aggregate gates (buy N total / spend $X total / weigh Ng total across the cart) need a
    // cart-wide composition this one-click, per-condition bundle can't guarantee -- a condition's
    // own quantity can satisfy this gate's unit check while still failing the real aggregate one.
    if (condition.totalQuantity?.enabled || condition.totalSpend?.enabled || condition.totalWeight?.enabled) {
      logBundleDiagnostic('unsupported_aggregate_threshold', { specialId, index });
      return [];
    }

    const productIds = unique([
      condition.productId,
      condition.enterpriseProductId,
      ...(condition.productIds ?? []),
      ...(condition.enterpriseProductIds ?? []),
    ]);

    // Several distinct IDs means "any matching product," not "require every one" -- can't power a
    // deterministic one-click bundle.
    if (productIds.length !== 1) {
      logBundleDiagnostic('ambiguous_product_match', { specialId, index, productIdCount: productIds.length });
      return [];
    }

    return [{ productId: productIds[0], quantity: resolveConditionQuantity(condition.quantity) }];
  });

  // All-or-nothing: one unresolved condition means no partial bundle ever ships.
  if (resolved.length !== conditions.length) {
    logBundleDiagnostic('condition_resolution_incomplete', {
      specialId,
      resolvedCount: resolved.length,
      conditionCount: conditions.length,
    });
    return [];
  }

  // Merge duplicate requirements for the same product ID into one, with summed quantity.
  // Preserves first-seen order for a stable item order downstream.
  const requirementsByProductId = new Map<string, BundleRequirement>();
  resolved.forEach(({ productId, quantity }) => {
    const existing = requirementsByProductId.get(productId);
    if (existing) {
      existing.quantity += quantity;
    } else {
      requirementsByProductId.set(productId, { productId, quantity });
    }
  });

  // Fewer distinct requirements than resolved conditions means some collapsed above.
  if (requirementsByProductId.size !== resolved.length) {
    logBundleDiagnostic('duplicate_condition_merged', { specialId });
  }

  return [...requirementsByProductId.values()];
}

/**
 * Whether `special` and `card` describe the same underlying discount. `SpecialMenuCard` is a
 * separate query from `special`'s own, joined back by ID here -- used only for display fallback
 * and `source` tagging in `buildBundle`, not the eligibility gate above.
 */
export function specialMatchesCard(special: Specials, card: SpecialMenuCard): boolean {
  return [special._id, special.sourceId, special.specialId].some(
    (id) => !!id && [card._id, card.posDiscountId].includes(id)
  );
}

/**
 * Unit price for `option` on `product` for the given menu type, or `null` if not determinable
 * (option not found, price missing/non-numeric/negative). `0` is a valid price, not missing.
 */
function getUnitPrice(product: Product, option: string, menuType: string): number | null {
  const optionIndex = product.Options?.indexOf(option) ?? -1;
  if (optionIndex < 0) {
    return null;
  }

  // A med-only or rec-only product legitimately has no price for the menu type it doesn't sell
  // on -- that resolves to "missing," never a silent fallback to the other menu type's price.
  const prices = menuType === MENU_TYPE_MED ? product.medicalPrices : product.recPrices;
  const price = prices?.[optionIndex];

  if (typeof price !== 'number' || !Number.isFinite(price) || price < 0) {
    return null;
  }

  return price;
}

/**
 * Client-side ESTIMATE only, for display before anything is in a cart -- NOT the real pricing
 * engine (packages/shared/order/bogo/calculate-bogo-discounts.js), which needs full cart/receipt
 * state (stacking behavior, stowed discounts, Big.js precision) this hook doesn't have. The live
 * POS price-cart call at add-to-cart/checkout time is the sole pricing authority; see `Bundle.price`.
 */
function getBundlePrice(originalPrice: number, itemCount: number, items: BundleItem[], special: Specials): number {
  const bundle = special.discountBundle;
  const value = Number(bundle?.value ?? 0);
  if (!bundle?.enabled || !bundle.discountType || !Number.isFinite(value)) {
    return originalPrice;
  }

  const appliesToAll = bundle.applyTo === 'ALL';

  switch (bundle.discountType) {
    case 'percentDiscount':
      return Math.max(0, originalPrice * (1 - value / 100));
    case 'dollarDiscount':
      if (appliesToAll) {
        return Math.max(0, originalPrice - value);
      }
      // EACH caps the discount at each line's OWN unit price -- matches getBogoIndividualDiscount
      // in packages/shared/order/utilities.js. Can't simplify to `originalPrice - value * itemCount`:
      // that spreads one line's excess discount onto another (e.g. a $2 item and a $10 item with
      // $5 off each is $0 + $5 = $5, not $12 - $10 = $2).
      return items.reduce((total, item) => total + Math.max(0, item.unitPrice - value) * item.quantity, 0);
    case 'targetPrice':
      return Math.max(0, value * (appliesToAll ? 1 : itemCount));
    default:
      return originalPrice;
  }
}

function getDiscountLabel(special: Specials): string {
  const bundle = special.discountBundle;
  const value = Number(bundle?.value ?? 0);
  if (!bundle?.discountType || !Number.isFinite(value)) {
    return 'Bundle discount applied in cart';
  }

  switch (bundle.discountType) {
    case 'percentDiscount':
      return `${value}% off bundle`;
    case 'dollarDiscount':
      return `$${value.toFixed(2)} off ${bundle.applyTo === 'ALL' ? 'bundle' : 'each item'}`;
    case 'targetPrice':
      return bundle.applyTo === 'ALL' ? `Bundle for $${value.toFixed(2)}` : `$${value.toFixed(2)} each`;
    default:
      return 'Bundle discount applied in cart';
  }
}

function getProductIdentity(product: Product): string | undefined {
  return product.id ?? product.enterpriseProductId ?? undefined;
}

/** Minimal shape `getBundleProgress` needs from a cart line item -- avoids a dependency on the
 * mobx `CartItem` class from this pure-function file. `key` is the same cart-item key CartItem
 * carries (see state/cart-item.ts), only needed for server corroboration below -- callers that
 * never pass `gamificationDetails` can omit it. */
type CartItemLike = { key?: string; product: { id?: string | null }; option: string; quantity: number };

function bundleItemIdentity(productId: string | null | undefined, option: string): string {
  return `${productId ?? ''}|${option}`;
}

/**
 * ECOMM-15153 progress corroboration: bundles are POS-integrated-only, same precondition as
 * Discount Gamification, and every cart mutation for those customers already runs a live
 * price-cart call that returns `gamificationDetails` for whichever discounts apply to the cart
 * (surfaced on the cart store as `discountGamificationDetails`) -- this reads that existing field
 * instead of re-deriving bundle progress purely client-side. Matches a `GamificationDetail` to
 * `bundle.special` by ID, then maps each `NUMBER_OF_ITEMS` satisfier's per-product `amount` back
 * to a bundle item by live cart-item key. Returns an empty map (pure client fallback) when the POS
 * hasn't surfaced this discount yet -- e.g. a non-POS dispensary, or the price-cart call hasn't
 * resolved since the item was added. Callers also take `Math.max` of this against client-computed
 * quantity as defense in depth -- see `getBundleProgress`'s `isCalculating` parameter for the
 * primary guard against stale server data.
 */
function getServerCorroboratedQuantities(
  bundle: Bundle,
  cartItems: CartItemLike[],
  gamificationDetails: GamificationDetail[] | null | undefined
): Map<string, number> {
  const specialIds = unique([bundle.special._id, bundle.special.sourceId, bundle.special.specialId]);
  // `gamificationDetails` is `cart.discountGamificationDetails`, read through the untyped
  // mobx-react provider context -- guard against a runtime `null` even though the static type
  // says array, since `.find` on `null` would otherwise crash this render.
  const matchingDetail = (gamificationDetails ?? []).find((detail) => specialIds.includes(String(detail.specialId)));
  const amountsByIdentity = new Map<string, number>();
  if (!matchingDetail) {
    return amountsByIdentity;
  }

  matchingDetail.thresholds.forEach((threshold) => {
    // Bundles are only ever quantity-of-specific-products requirements (see
    // getSpecialProductRequirements) -- a dollar/weight satisfier on the same special can't
    // corroborate a per-item unit count.
    if (threshold.thresholdType !== GAMIFICATION_THRESHOLD_TYPES.NUMBER_OF_ITEMS) {
      return;
    }
    threshold.applicableProducts.forEach(({ productKey, amount }) => {
      const cartItem = cartItems.find((item) => item.key === productKey);
      if (cartItem) {
        amountsByIdentity.set(bundleItemIdentity(cartItem.product.id, cartItem.option), amount);
      }
    });
  });

  return amountsByIdentity;
}

/**
 * How much of `bundle` a shopper's cart already satisfies. Per item: units already in cart
 * (capped at the required quantity) and units still needed; `remaining` sums every item's
 * shortfall. Shared by the cart interstitial (ECOMM-15167), the cart-drawer per-item widget
 * (ECOMM-15153), and the PDP bundle-offers progress ring (ECOMM-15148) -- cart-progress math
 * lives here once, not duplicated per consumer. `gamificationDetails` (default `[]`) lets callers
 * pass `cart.discountGamificationDetails` for server corroboration; omit it to stay pure
 * client-computed.
 *
 * `isCalculating` (default `false`) should be `cart.isCalculating`: `discountGamificationDetails`
 * is only replaced once the in-flight price-cart recalculation resolves (see
 * `state/cart-calc-actions/calculation-actions.js`), so between a cart-quantity change and that
 * resolution the server detail can be stale in EITHER direction -- not just the stale-lower case
 * `Math.max` below guards against, but also stale-higher (e.g. the shopper just lowered a bundle
 * item's quantity and the server detail still reflects the old, higher count, which would
 * overstate progress/falsely show "bundle complete!"). Passing `isCalculating: true` ignores the
 * server detail entirely for that render and falls back to pure client math, which is always
 * current.
 */
export function getBundleProgress(
  bundle: Bundle,
  cartItems: CartItemLike[],
  gamificationDetails: GamificationDetail[] | null | undefined = [],
  isCalculating = false
): BundleProgress {
  const corroboratedQuantities = isCalculating
    ? new Map<string, number>()
    : getServerCorroboratedQuantities(bundle, cartItems, gamificationDetails);

  const itemProgress = bundle.items.map((item) => {
    const identity = bundleItemIdentity(item.product.id, item.option);
    const clientQuantityInCart = cartItems.reduce((total, cartItem) => {
      const matches = cartItem.product.id === item.product.id && cartItem.option === item.option;
      return matches ? total + Number(cartItem.quantity) : total;
    }, 0);
    // Take the larger of the two counts: the POS corroboration can lag a cart mutation that
    // hasn't round-tripped through a price-cart call yet, so a stale-lower server amount must
    // never regress progress the shopper can already see reflected in their own cart contents.
    const serverQuantityInCart = corroboratedQuantities.get(identity);
    const quantityInCart =
      serverQuantityInCart === undefined ? clientQuantityInCart : Math.max(serverQuantityInCart, clientQuantityInCart);
    const cappedQuantityInCart = Math.min(quantityInCart, item.quantity);
    return { item, quantityInCart: cappedQuantityInCart, quantityNeeded: item.quantity - cappedQuantityInCart };
  });

  return {
    itemProgress,
    remaining: itemProgress.reduce((total, progress) => total + progress.quantityNeeded, 0),
  };
}
/**
 * Assembles the final `Bundle` for `special` against the catalog slice in `products` (see
 * use-bundles.ts), or `null` if any requirement can't resolve to a real, singly-priced product --
 * same all-or-nothing rule as the eligibility gate.
 *
 * A requirement is dropped (rejecting the whole bundle) when: its product ID matches nothing in
 * `products`; the matched product has zero or 2+ priced `Option`s; or the option has no usable
 * price for `menuType`. Two requirements resolving to the same real product (one via `productId`,
 * another via `enterpriseProductId`) merge into one line item with summed quantity.
 */
export function buildBundle({
  special,
  menuCard,
  products,
  menuType,
}: {
  special: Specials;
  menuCard?: SpecialMenuCard;
  products: Product[];
  menuType: string;
}): Bundle | null {
  const specialId = getSpecialId(special);
  const requirements = getSpecialProductRequirements(special);
  if (!requirements.length) {
    return null;
  }

  const items: BundleItem[] = [];
  const itemIndexByIdentity = new Map<string, number>();

  // `every` (not a loop, per lint convention) short-circuits on the first unresolvable
  // requirement -- the bundle is all-or-nothing, so there's no value in continuing past it.
  const resolvedEveryRequirement = requirements.every((requirement) => {
    const product = products.find((candidate) =>
      [candidate.id, candidate.enterpriseProductId].includes(requirement.productId)
    );

    if (!product) {
      logBundleDiagnostic('product_not_found', { specialId, productId: requirement.productId });
      return false;
    }

    // Identity independent of which ID namespace resolved it (productId vs enterpriseProductId) --
    // two requirements landing on the same real product must not become two line items.
    const identity = getProductIdentity(product) ?? requirement.productId;
    const existingIndex = itemIndexByIdentity.get(identity);
    if (existingIndex !== undefined) {
      logBundleDiagnostic('duplicate_item_merged', { specialId, productId: identity });
      items[existingIndex].quantity += requirement.quantity;
      return true;
    }

    const options = (product.Options ?? []).filter((option): option is string => !!option);
    if (options.length !== 1) {
      logBundleDiagnostic('invalid_option_count', { specialId, productId: identity, optionCount: options.length });
      return false;
    }
    const [option] = options;

    const unitPrice = getUnitPrice(product, option, menuType);
    if (unitPrice === null) {
      logBundleDiagnostic('missing_unit_price', { specialId, productId: identity, option, menuType });
      return false;
    }

    itemIndexByIdentity.set(identity, items.length);
    items.push({ product, productId: requirement.productId, option, quantity: requirement.quantity, unitPrice });
    return true;
  });

  if (!resolvedEveryRequirement) {
    return null;
  }

  const originalPrice = items.reduce((total, item) => total + item.unitPrice * item.quantity, 0);
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const source: BundleSource = special.source === 'POS' && menuCard?.isBundledDiscount ? 'brand-funded' : 'native';

  return {
    id: String(menuCard?._id ?? special._id),
    name: menuCard?.menuDisplayName ?? special.menuDisplayName ?? special.name ?? 'Bundle',
    description: menuCard?.menuDisplayDescription ?? special.menuDisplayDescription ?? special.description ?? '',
    imageUrl: menuCard?.menuDisplayImage ?? special.menuDisplayImage,
    source,
    special,
    menuCard,
    items,
    originalPrice,
    price: getBundlePrice(originalPrice, itemCount, items, special),
    discountLabel: getDiscountLabel(special),
  };
}
